<template>
  <bm-page-footer>
    <template #left>
      <bm-btn
        button-type="secondary"
        :icon="isMobileDisplay"
        data-qa="wizard_close"
        @click="closeWizard"
        ><template v-if="!isMobileDisplay">{{ $m.close }}</template
        ><bm-icon v-if="isMobileDisplay" icon="Clear" />
      </bm-btn>
    </template>

    <bm-btn
      v-if="numSteps > 1"
      key="wizard_previous"
      btn-class="mr-2"
      button-type="secondary"
      :disabled="!canReturn"
      :icon="isMobileDisplay"
      :tooltip="$m.previous_step_hint"
      data-qa="wizard_previous"
      @click="previousWizard"
      ><template v-if="!isMobileDisplay">{{ $m.previous_step }}</template
      ><bm-icon v-if="isMobileDisplay" icon="ArrowBack" />
    </bm-btn>

    <bm-btn
      v-if="numSteps > 1"
      key="wizard_next"
      btn-class="mr-2"
      button-type="secondary"
      :disabled="!canContinue"
      :icon="isMobileDisplay"
      :tooltip="$m.next_step_hint"
      data-qa="wizard_next"
      @click="nextWizard"
      ><template v-if="!isMobileDisplay">{{ $m.next_step }}</template
      ><bm-icon v-if="isMobileDisplay" icon="ArrowForward" />
    </bm-btn>

    <bm-btn
      key="wizard_finish"
      :disabled="!canComplete"
      :tooltip="$m.finish_hint"
      btn-class="mr-2"
      data-qa="wizard_finish"
      @click="finishWizard"
    >
      {{ finishButtonText }}
    </bm-btn>
  </bm-page-footer>
</template>

<script setup lang="ts">
import BmBtn from "@/components/controls/input/BmBtn.vue";
import BmPageFooter from "@/components/pages/BmPageFooter.vue";
import type { WizardRegistry } from "./useWizardRegistry";
import { screenSize } from "@/vue23switching/screenSize";
import { computed } from "vue";

interface BmWizardFooterProps {
  registry: WizardRegistry;
  finishButtonText: string;
}

interface BmWizardFooterEvents {
  (event: "close"): void;
  (event: "previous"): void;
  (event: "next"): void;
  (event: "finish"): void;
}

const props = defineProps<BmWizardFooterProps>();

const emit = defineEmits<BmWizardFooterEvents>();

const numSteps = computed(
  () => props.registry.steps.value.filter((x) => x.enabled).length
);
const canReturn = computed(() => props.registry.canReturn());
const canContinue = computed(() => props.registry.hasMoreEnabledSteps());
const canComplete = computed(() => props.registry.canComplete());
const isMobileDisplay = computed(() => screenSize.xs);

const closeWizard = () => {
  emit("close");
};

const previousWizard = () => {
  emit("previous");
};

const nextWizard = () => {
  emit("next");
};

const finishWizard = () => {
  // BREW-1794
  // NB: I have no idea what causes this, but when a new session is started then the first time a user navigates
  // to a wizard a native click event is thrown which fires this method.
  // If the user has already visited a wizard then this doesn't seem to happen.
  // To prevent this from validating the form before it's loaded, check this method can logically be called before
  // firing a Wizard Finished event.
  if (props.registry.canComplete()) {
    emit("finish");
  }
};
</script>
